/**
 * Choice Option Matcher Utility
 *
 * Validates and matches selected values against control options, mapped codes, and special options (all/none/other).
 * Ensures invalid or stale values (e.g. legacy slugs) are cleanly sanitized and removed from UI extensions when saving
 * templates, without affecting legitimate options or medical code mappings.
 */

import type { IControlCard } from '../interfaces/fhir-converter.interfaces';

/** Shape of an option object in choice controls across Designer, Conductor, and Stage. */
export interface ChoiceOptionLike {
  id?: string | number;
  label?: string;
  text?: string;
  display?: string;
  displayText?: string;
  name?: string;
  value?: unknown;
  code?: string | number;
  mappedCodes?: Array<{
    code?: string | number;
    display?: string;
    system?: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

/**
 * Checks if a candidate value matches an individual option, taking into account value, label, text, display, code, and
 * mappedCodes.
 */
export function isOptionMatch(opt: ChoiceOptionLike, candidateValue: unknown): boolean {
  if (candidateValue === null || candidateValue === undefined || candidateValue === '') {
    return false;
  }

  // 1. Primitive direct equality
  if (opt.value !== undefined && opt.value !== null && opt.value === candidateValue) {
    return true;
  }

  // 2. Extract string representation of candidate value (supporting structured choice values)
  const candidateStr =
    typeof candidateValue === 'object' && candidateValue !== null
      ? String(
          (candidateValue as { code?: unknown; value?: unknown; display?: unknown }).code ??
            (candidateValue as { code?: unknown; value?: unknown; display?: unknown }).value ??
            (candidateValue as { code?: unknown; value?: unknown; display?: unknown }).display ??
            ''
        )
      : String(candidateValue);

  if (!candidateStr) return false;
  const candidateLower = candidateStr.trim().toLowerCase();

  // Match opt.value
  if (opt.value !== undefined && opt.value !== null) {
    const optValStr = String(opt.value);
    if (optValStr === candidateStr || optValStr.trim().toLowerCase() === candidateLower) {
      return true;
    }
  }

  // Match display labels (label, display, displayText, text, name)
  const displayLabels = [opt.label, opt.display, opt.displayText, opt.text, opt.name].filter(
    (l): l is string => typeof l === 'string' && l.trim().length > 0
  );
  for (const label of displayLabels) {
    if (label === candidateStr || label.trim().toLowerCase() === candidateLower) {
      return true;
    }
  }

  // Match opt.code (medical code or custom code)
  if (opt.code !== undefined && opt.code !== null) {
    const codeStr = String(opt.code);
    if (codeStr === candidateStr || codeStr.trim().toLowerCase() === candidateLower) {
      return true;
    }
  }

  // Match opt.id
  if (opt.id !== undefined && opt.id !== null) {
    const idStr = String(opt.id);
    if (idStr === candidateStr || idStr.trim().toLowerCase() === candidateLower) {
      return true;
    }
  }

  // Match against mappedCodes
  // Crucial: mappedCodes must never be modified, and if candidate matches
  // an entry's code or display in mappedCodes, it is considered a valid match.
  if (Array.isArray(opt.mappedCodes) && opt.mappedCodes.length > 0) {
    for (const mc of opt.mappedCodes) {
      if (!mc || typeof mc !== 'object') continue;
      if (mc.code !== undefined && mc.code !== null) {
        const mcCodeStr = String(mc.code);
        if (mcCodeStr === candidateStr || mcCodeStr.trim().toLowerCase() === candidateLower) {
          return true;
        }
      }
      if (mc.display && typeof mc.display === 'string') {
        if (mc.display === candidateStr || mc.display.trim().toLowerCase() === candidateLower) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Checks if candidateValue matches a valid special option ('other', 'all', 'none') based on control configuration or
 * options list.
 */
export function isSpecialOptionMatch(
  candidateValue: unknown,
  control: IControlCard,
  options: ChoiceOptionLike[]
): boolean {
  if (candidateValue === null || candidateValue === undefined || candidateValue === '') {
    return false;
  }
  const candidateLower = String(candidateValue).trim().toLowerCase();

  const componentProps = (control.componentProps as Record<string, unknown> | undefined) ?? {};
  const rootProps = control as Record<string, unknown>;

  // Check 'other'
  if (candidateLower === 'other') {
    const hasOther =
      componentProps['hasOther'] === true ||
      componentProps['hasOthers'] === true ||
      componentProps['showOtherOption'] === true ||
      rootProps['hasOther'] === true ||
      rootProps['hasOthers'] === true ||
      rootProps['showOtherOption'] === true ||
      options.some(
        (opt) =>
          opt.id === 'other' ||
          String(opt.value).toLowerCase() === 'other' ||
          (typeof opt.label === 'string' && opt.label.toLowerCase() === 'other')
      );
    if (hasOther) return true;
  }

  // Check 'all'
  if (candidateLower === 'all') {
    const hasAll =
      componentProps['showAllRadio'] === true ||
      componentProps['showAllDropdown'] === true ||
      componentProps['showAllCheckbox'] === true ||
      componentProps['showAll'] === true ||
      rootProps['showAllRadio'] === true ||
      rootProps['showAllDropdown'] === true ||
      rootProps['showAllCheckbox'] === true ||
      rootProps['showAll'] === true ||
      options.some(
        (opt) =>
          opt.id === 'all' ||
          String(opt.value).toLowerCase() === 'all' ||
          (typeof opt.label === 'string' && opt.label.toLowerCase() === 'all')
      );
    if (hasAll) return true;
  }

  // Check 'none'
  if (candidateLower === 'none') {
    const hasNone =
      componentProps['showNoneRadio'] === true ||
      componentProps['showNoneDropdown'] === true ||
      componentProps['showNoneCheckbox'] === true ||
      componentProps['showNone'] === true ||
      rootProps['showNoneRadio'] === true ||
      rootProps['showNoneDropdown'] === true ||
      rootProps['showNoneCheckbox'] === true ||
      rootProps['showNone'] === true ||
      options.some(
        (opt) =>
          opt.id === 'none' ||
          String(opt.value).toLowerCase() === 'none' ||
          (typeof opt.label === 'string' && opt.label.toLowerCase() === 'none')
      );
    if (hasNone) return true;
  }

  return false;
}

/**
 * Determines whether a given value is valid for the options configured on a control. Returns true if the value matches
 * an option (by value, label, code, or mappedCodes) or matches an enabled special option (all/none/other).
 */
export function isValidChoiceValue(value: unknown, options: ChoiceOptionLike[], control: IControlCard): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (isSpecialOptionMatch(value, control, options)) {
    return true;
  }
  if (!Array.isArray(options) || options.length === 0) {
    return false;
  }
  return options.some((opt) => isOptionMatch(opt, value));
}

/** Finds the matching option for a given value, or null if no option matches. */
export function findMatchingChoiceOption(value: unknown, options: ChoiceOptionLike[]): ChoiceOptionLike | null {
  if (value === null || value === undefined || value === '' || !Array.isArray(options)) {
    return null;
  }
  return options.find((opt) => isOptionMatch(opt, value)) ?? null;
}

/**
 * Inspects a control's UI config object before serialization into the UI extension. If the control is an option-based
 * control (such as radio-group) and has a 'value' property, checks if that value matches any active option or enabled
 * special option. If the value does NOT match any option, it is removed from the config object.
 *
 * NOTE: Mapped codes, options arrays, and medical coding metadata are strictly preserved.
 */
export function sanitizeChoiceValueInConfig(
  control: IControlCard,
  metadata: { supportsOptions?: boolean; componentType?: string } | null,
  config: Record<string, unknown>
): void {
  if (!('value' in config)) {
    return;
  }

  const controlType = String((control as Record<string, unknown>)['controlType'] ?? control.type ?? '');
  const isOptionControl =
    controlType === 'radio-group' ||
    controlType === 'radio' ||
    metadata?.componentType === 'HxRadioGroup' ||
    metadata?.supportsOptions === true ||
    Array.isArray(control.options) ||
    Array.isArray((control.componentProps as Record<string, unknown> | undefined)?.['options']);

  if (!isOptionControl) {
    // Non-option controls (e.g. text-input, date-picker) preserve their default value
    return;
  }

  const componentProps = control.componentProps as Record<string, unknown> | undefined;
  const options = (componentProps?.['options'] ?? control.options ?? []) as ChoiceOptionLike[];
  const candidateValue = config['value'];

  if (!isValidChoiceValue(candidateValue, options, control)) {
    delete config['value'];
  }
}
